import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import WebMercatorViewport from 'viewport-mercator-project';
import classNames from 'classnames';
import { InlineLoading, Checkbox } from '@carbon/react';
import { Map, PolygonArea } from 'components/map';
import { getMapDefaultConfig } from 'components/map/atoms';
import {
  buildPolygonStyle,
  calculateBounds,
  extractCoordinatesFromGeometry,
} from 'lib/map';
import { atom } from 'jotai';
import { MapUserConfig } from 'components/map/types';
import { Popup } from 'react-map-gl';
import {
  useMapHoverHandler,
  LayerProcessor,
} from 'lib/hooks/useMapHoverHandler';
import style from './comparison-map.module.scss';

const MAP_THUMBNAIL_WIDTH = 400;
const MAP_THUMBNAIL_HEIGHT = 260;
const MIN_ZOOM_LEVEL = 10;
const VIEWPORT_PADDING = 40;
const INTERSECTION_MARGIN = '50px';

// Map color constants - Mapbox doesn't support Carbon tokens, so we define colors here
const MAP_COLORS = {
  validator: { fill: 'rgb(220, 38, 38)', line: 'rgb(185, 28, 28)' },
  eta: { fill: 'rgb(37, 99, 235)', line: 'rgb(29, 78, 216)' },
} as const;

const VALIDATOR_STYLE = buildPolygonStyle({
  color: MAP_COLORS.validator.fill,
  lineColor: MAP_COLORS.validator.line,
  fillOpacity: 0.4,
  lineOpacity: 1,
  lineWidth: 2,
});

const ETA_STYLE = buildPolygonStyle({
  color: MAP_COLORS.eta.fill,
  lineColor: MAP_COLORS.eta.line,
  fillOpacity: 0.4,
  lineOpacity: 1,
  lineWidth: 2,
});

type GeometryFeature = {
  type: 'validator' | 'shape';
  name: string;
  id: string;
};

interface ComparisonMapThumbnailProps {
  validatorGeometry: GeoJSON.Geometry | null;
  shapeGeometries: GeoJSON.Geometry[] | null;
  geometryName: string;
  shapeNames: string[];
}

export const ComparisonMapThumbnail = ({
  validatorGeometry,
  shapeGeometries,
  geometryName,
  shapeNames,
}: ComparisonMapThumbnailProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [showValidator, setShowValidator] = useState(true);
  const [showBorders, setShowBorders] = useState(true);

  const [showShapes, setShowShapes] = useState(true);

  const mapConfigAtom = useMemo(() => {
    const defaultConfig = getMapDefaultConfig();
    return atom({
      ...defaultConfig,
      wells: { ...defaultConfig.wells, checked: false },
      wellsV2: { ...defaultConfig.wellsV2, checked: false },
      developmentAreas: {
        ...defaultConfig.developmentAreas,
        options: {
          ...defaultConfig.developmentAreas.options,
          horizontalUnits: false,
          verticalUnits: false,
          fieldUnits: false,
          pricing: false,
        } as MapUserConfig['developmentAreas']['options'],
      },
      titleEntityAssets: { ...defaultConfig.titleEntityAssets, checked: false },
      basinTier: { ...defaultConfig.basinTier, checked: false },
      scrollZoom: { checked: true },
      popups: { checked: false },
      focusAreas: { checked: false, selections: {} },
    });
  }, []);

  const layerProcessors: LayerProcessor<GeometryFeature>[] = useMemo(
    () => [
      {
        prefix: `polygon-fill-validator-${geometryName}`,
        isVisible: showValidator,
        extractId: () => geometryName,
        buildFeature: (name) => ({
          type: 'validator' as const,
          name,
          id: name,
        }),
      },
      ...shapeNames.map((shapeName, idx) => ({
        prefix: `polygon-fill-shape-${shapeName}-${idx}`,
        isVisible: showShapes,
        extractId: () => String(idx),
        buildFeature: () => ({
          type: 'shape' as const,
          name: shapeName,
          id: String(idx),
        }),
      })),
    ],
    [geometryName, showValidator, shapeNames, showShapes]
  );

  const { hoverInfo, onMouseMove, onMouseLeave } =
    useMapHoverHandler(layerProcessors);

  const getFeatureColors = useCallback(
    (
      type: 'validator' | 'shape'
    ): { fillColor: string; borderColor: string } => {
      if (type === 'validator') {
        return {
          fillColor: MAP_COLORS.validator.fill,
          borderColor: MAP_COLORS.validator.line,
        };
      }
      return {
        fillColor: MAP_COLORS.eta.fill,
        borderColor: MAP_COLORS.eta.line,
      };
    },
    []
  );

  const interactiveLayerIds = useMemo(() => {
    const ids: string[] = [];
    if (showValidator && validatorGeometry) {
      ids.push(`polygon-fill-validator-${geometryName}`);
    }
    if (showShapes && shapeGeometries) {
      shapeNames.forEach((shapeName, idx) => {
        if (shapeGeometries[idx]) {
          ids.push(`polygon-fill-shape-${shapeName}-${idx}`);
        }
      });
    }
    return ids;
  }, [
    showValidator,
    validatorGeometry,
    geometryName,
    showShapes,
    shapeNames,
    shapeGeometries,
  ]);

  const mapViewState = useMemo(() => {
    if (
      !validatorGeometry &&
      (!shapeGeometries || shapeGeometries.length === 0)
    )
      return null;

    try {
      const coordinates = extractAllCoordinates(
        validatorGeometry,
        shapeGeometries
      );

      if (coordinates.length === 0) return null;

      const bounds = calculateBounds(coordinates);
      const mercator = new WebMercatorViewport({
        width: MAP_THUMBNAIL_WIDTH,
        height: MAP_THUMBNAIL_HEIGHT,
      });

      const viewState = mercator.fitBounds(
        [
          [bounds.getWest(), bounds.getSouth()],
          [bounds.getEast(), bounds.getNorth()],
        ],
        { padding: VIEWPORT_PADDING }
      );

      return {
        longitude: viewState.longitude,
        latitude: viewState.latitude,
        zoom: Math.max(viewState.zoom, MIN_ZOOM_LEVEL),
      };
    } catch (error) {
      console.error('Error calculating map view:', error);
      return null;
    }
  }, [validatorGeometry, shapeGeometries]);

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
        });
      },
      { rootMargin: INTERSECTION_MARGIN }
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  if (
    !validatorGeometry &&
    (!shapeGeometries || shapeGeometries.length === 0)
  ) {
    return <div className={style.empty}>No geometry data available</div>;
  }

  return (
    <div ref={containerRef} className={style.container}>
      <div className={style.legendKey}>
        <div className={style.legendKeyLeft}>
          <button
            type="button"
            className={classNames(style.legendKeyButton, {
              [style.legendKeyButtonInactive]: !showValidator,
            })}
            onClick={() => setShowValidator((prev) => !prev)}
          >
            <div className={style.legendColorValidator} />
            <span>
              Title Validator Output Shape ({validatorGeometry ? 1 : 0})
            </span>
          </button>
          <button
            type="button"
            className={classNames(style.legendKeyButton, {
              [style.legendKeyButtonInactive]: !showShapes,
            })}
            onClick={() => setShowShapes(!showShapes)}
          >
            <div className={style.legendColorEta} />
            <span>
              Entity Title Asset Shape ({shapeGeometries?.length ?? 0})
            </span>
          </button>
        </div>
        <div className={style.legendKeyRight}>
          <Checkbox
            id={`toggle-borders-${geometryName}-${shapeNames.join('-')}`}
            labelText="Hide Map Borders"
            checked={!showBorders}
            onChange={(_evt, { checked }: { checked: boolean }) =>
              setShowBorders(!checked)
            }
          />
        </div>
      </div>

      {isVisible && mapViewState ? (
        <div className={style.map}>
          <Map
            longitude={mapViewState.longitude}
            latitude={mapViewState.latitude}
            zoom={mapViewState.zoom}
            mapConfigAtom={mapConfigAtom}
            showControls={true}
            showMapFilters={true}
            interactiveLayerIds={interactiveLayerIds}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
          >
            {validatorGeometry && showValidator && (
              <PolygonArea
                id={`validator-${geometryName}`}
                geometry={validatorGeometry}
                style={VALIDATOR_STYLE}
                showBorder={showBorders}
              />
            )}
            {showShapes &&
              shapeGeometries?.map((geometry, idx) => (
                <PolygonArea
                  key={`shape-${shapeNames[idx]}-${idx}`}
                  id={`shape-${shapeNames[idx]}-${idx}`}
                  geometry={geometry}
                  style={ETA_STYLE}
                  showBorder={showBorders}
                />
              ))}

            {/* Hover tooltip */}
            {hoverInfo && (
              <Popup
                longitude={hoverInfo.longitude}
                latitude={hoverInfo.latitude}
                closeButton={false}
                closeOnClick={false}
                className={style.hoverPopup}
              >
                <div className={style.popupContent}>
                  {hoverInfo.features.map((feature) => {
                    const { fillColor, borderColor } = getFeatureColors(
                      feature.type
                    );
                    return (
                      <div key={feature.id} className={style.popupItem}>
                        <span
                          className={style.legendSquare}
                          style={{
                            backgroundColor: fillColor,
                            borderColor: borderColor,
                          }}
                        />
                        <span>{feature.name}</span>
                      </div>
                    );
                  })}
                </div>
              </Popup>
            )}
          </Map>
        </div>
      ) : (
        <div className={style.placeholder}>
          <InlineLoading description="Loading map..." />
        </div>
      )}
    </div>
  );
};

function extractAllCoordinates(
  validatorGeometry: GeoJSON.Geometry | null,
  shapeGeometries: GeoJSON.Geometry[] | null
): number[][] {
  const allCoords: number[][] = [];

  if (validatorGeometry) {
    allCoords.push(...extractCoordinatesFromGeometry(validatorGeometry));
  }

  if (shapeGeometries) {
    shapeGeometries.forEach((geom) => {
      allCoords.push(...extractCoordinatesFromGeometry(geom));
    });
  }

  return allCoords;
}
